<template>
    <section class="orders">
        <main :class="{ 'expand-main': false, main:true }">
            <div class='container-fluid'>
                <div class="d-flex align-items-center flex-wrap mb-2">
                    <h2 class="mb-0 mr-3">Import error</h2>
                    <button
                        v-if="isAdmin"
                        type="button"
                        class="btn btn-warning btn-sm"
                        :disabled="reportSending || !dangerOrderCount"
                        @click="reportProblems"
                    >
                        <span v-if="reportSending" class="spinner-border spinner-border-sm mr-1" />
                        Report problems
                    </button>
                </div>
                <p v-if="reportMessage" class="small mb-2" :class="reportMessageClass">
                    {{ reportMessage }}
                </p>
                <Tabs
                    :tabSelected="tabSelected"
                    :tabs="importTabs"
                    @change="changeTab($event)"
                />
                <OrphanLinksTab v-if="tabSelected === ORPHAN_LINKS_TAB" />
                <BookingIntakeTab v-if="tabSelected === BOOKING_INTAKE_TAB" />
                <DomainsTab v-if="tabSelected === DOMAINS_TAB" />
                <div
                    v-if="!isStandaloneImportTab && (pipelineLoading || importPipeline)"
                    class="alert alert-light border small mb-3 mx-2 py-2"
                >
                    <div
                        v-if="pipelineLoading && !importPipeline"
                        class="d-flex align-items-center text-muted"
                    >
                        <span
                            class="spinner-border spinner-border-sm mr-2"
                            role="status"
                            aria-hidden="true"
                        />
                        Loading SalesRidge import pipeline…
                    </div>
                    <template v-else-if="importPipeline">
                    <div class="d-flex align-items-start justify-content-between">
                        <div class="d-flex align-items-center flex-wrap">
                            <strong class="mr-2">SalesRidge import pipeline</strong>
                            <span v-if="importPipeline.is_importing_now" class="badge badge-warning text-dark">
                                import.php running
                            </span>
                            <span
                                v-else-if="importPipeline.needs_generatetable"
                                class="badge badge-info text-white"
                            >
                                needs generatetable
                            </span>
                            <span v-else class="badge badge-secondary">idle</span>
                        </div>
                        <button
                            type="button"
                            class="btn btn-sm btn-outline-secondary py-0 px-2"
                            title="Refresh pipeline stats"
                            :disabled="pipelineRefreshing"
                            @click="refreshImportPipeline"
                        >
                            <span
                                v-if="pipelineRefreshing"
                                class="spinner-border spinner-border-sm"
                                role="status"
                                aria-hidden="true"
                            />
                            <i v-else class="icon-refresh" aria-hidden="true" />
                        </button>
                    </div>
                    <div
                        v-if="pipelineReadyForExportLine"
                        class="mt-2"
                    >
                        {{ pipelineReadyForExportLine }}
                    </div>
                    <div class="mt-2">
                        <div class="d-flex align-items-center flex-wrap mb-1">
                            <strong class="mr-2">Import queue</strong>
                            <span
                                v-if="importPipeline"
                                class="badge mr-2"
                                :class="pipelineQueueNightMode ? 'badge-dark' : 'badge-light border'"
                            >
                                {{ pipelineQueueNightMode ? "night · 40 slots" : "day · 20 slots" }}
                            </span>
                            <span class="text-muted">
                                {{ pipelineQueueSlotsLabel }}
                            </span>
                        </div>
                        <div
                            class="pipeline-slot-row d-flex flex-wrap"
                            :class="{ 'pipeline-slot-row--night': pipelineQueueMax > 20 }"
                            role="img"
                            :aria-label="pipelineQueueSlotsLabel"
                        >
                            <span
                                v-for="slot in pipelineQueueSlots"
                                :key="'slot-' + slot.position"
                                class="pipeline-slot"
                                :class="[
                                    slot.occupied ? 'pipeline-slot--occupied' : 'pipeline-slot--free',
                                    slot.nightSlot ? 'pipeline-slot--night-extra' : '',
                                ]"
                                :title="slot.title"
                            >
                                {{ slot.position }}
                            </span>
                        </div>
                        <div class="mt-1 text-muted">
                            {{ pipelineThroughputLine }}
                        </div>
                        <div
                            v-if="pipelineHourChartData"
                            class="mt-2 pipeline-hour-chart-wrap"
                        >
                            <div class="text-muted mb-1">
                                <strong>Last hour</strong>
                                — batches imported per 5 min (oldest → now)
                            </div>
                            <line-chart
                                :key="pipelineHourChartKey"
                                :height="72"
                                :chart-data="pipelineHourChartData"
                                :options="pipelineHourChartOptions"
                            />
                        </div>
                    </div>
                    <div class="mt-1 text-muted">
                        {{ pipelineImportLine }}
                    </div>
                    <div class="mt-1 text-muted">
                        {{ pipelineEnqueueLine }}
                    </div>
                    <div
                        v-if="pipelineActiveText"
                        class="mt-1"
                    >
                        {{ pipelineActiveText }}
                    </div>
                    </template>
                </div>
                <div
                    v-if="!loading && tabSelected === ACTIVE_IMPORTING_TAB"
                    class="alert alert-light border small mb-3 mx-2 py-2"
                >
                    <strong class="d-block mb-1">Campaign running/preparing waiting SR</strong>
                    <span class="text-muted d-block mb-2">
                        Running or Preparing orders with SalesRidge batches still to import.
                        Sorted oldest → newest by when the order entered its current status.
                    </span>
                    <ul class="list-unstyled mb-0 small text-muted">
                        <li class="mb-1">
                            <span class="import-tone-swatch import-tone-swatch--launch" /> Red — no SR yet; Launch available
                        </li>
                        <li class="mb-1">
                            <span class="import-tone-swatch import-tone-swatch--blocked" /> Yellow — SR blocked (e.g. 50k limit); relaunch not allowed
                        </li>
                        <li class="mb-0">
                            <span class="import-tone-swatch import-tone-swatch--running" /> Green — already has contacts / sending; relaunch not allowed
                        </li>
                    </ul>
                </div>
                <div
                    v-if="!loading && tabSelected === IMPORT_ERRORS_TAB"
                    class="alert alert-light border small mb-3 mx-2 py-2 magnifier-legend"
                >
                    <strong class="d-block mb-2">Legend</strong>
                    <ul class="list-unstyled mb-0">
                        <li class="d-flex align-items-start mb-2">
                            <span
                                class="situation-tone-legend situation-tone--ok flex-shrink-0 mr-2"
                                aria-hidden="true"
                            >
                                <i class="icon-search" />
                            </span>
                            <span>
                                <strong>Green magnifier</strong> — on track. Import is done and sends are
                                scheduled or already going out. The summary box when you open the
                                magnifier matches this color. Individual steps may still show
                                pending (e.g. a few contacts not yet in campaigns).
                            </span>
                        </li>
                        <li class="d-flex align-items-start mb-2">
                            <span
                                class="situation-tone-legend situation-tone--warning flex-shrink-0 mr-2"
                                aria-hidden="true"
                            >
                                <i class="icon-search" />
                                <i class="icon-exclamation-triangle situation-tone-badge situation-tone-badge--warning" />
                            </span>
                            <span>
                                <strong>Yellow magnifier</strong> — still waiting, but the pipeline
                                is moving. Typical cases: SalesRidge import within the first 24h,
                                batches in the import queue, or contacts not yet assigned before
                                sends start. Hover or open the magnifier — the
                                <strong>headline</strong> tells you which case. The
                                <i class="icon-exclamation-triangle icon-small" aria-hidden="true" />
                                appears inside the button only after the color finishes loading.
                                <em>Not</em> used for 0-lead searches (those are red).
                            </span>
                        </li>
                        <li class="d-flex align-items-start mb-2">
                            <span
                                class="situation-tone-legend situation-tone--danger flex-shrink-0 mr-2"
                                aria-hidden="true"
                            >
                                <i class="icon-search" />
                                <i class="icon-exclamation-triangle situation-tone-badge situation-tone-badge--danger" />
                            </span>
                            <span>
                                <strong>Red magnifier</strong> — action needed. Includes:
                                SalesRidge search returned <strong>0 leads</strong> (filters too
                                narrow — widen targeting and relaunch), import stuck after 24h,
                                or campaign setup blocking sends (mailbox, booking link).
                                Open the magnifier for the exact headline and next step.
                            </span>
                        </li>
                        <li class="d-flex align-items-start mb-0">
                            <span
                                class="situation-tone-legend situation-tone--danger flex-shrink-0 mr-2"
                                aria-hidden="true"
                            >
                                <i class="icon-search" />
                                <i class="icon-exclamation-triangle situation-tone-badge situation-tone-badge--danger" />
                            </span>
                            <span>
                                <strong>Launch failed</strong> badge on the order name — same red
                                magnifier. Automated SalesRidge search failed; this order is
                                <em>skipped by launch cron</em>. Open the order, review filters,
                                and relaunch manually.
                            </span>
                        </li>
                    </ul>
                </div>
                <div
                    v-if="!loading && tabSelected === ACTIVE_IMPORTING_TAB"
                    class="alert alert-light border small mb-3 mx-2 py-2 import-progress-legend"
                >
                    <strong class="d-block mb-2">Import timeline</strong>
                    <p class="text-muted mb-2">
                        Gray row = warmup order · Red row = no SalesRidge order launched
                    </p>
                    <p class="text-muted mb-3">
                        Each batch of contacts moves through these steps in order. The colored bar
                        shows how many batches are at each step <em>right now</em> (often several
                        colors at once). Click a row’s bar for details.
                    </p>
                    <div class="import-progress-timeline" role="img" aria-label="Import timeline from SalesRidge to Oliverlist">
                        <div class="import-progress-timeline__step">
                            <span class="import-progress-timeline__chip import-progress-timeline__chip--pending">
                                Gray
                            </span>
                            <span class="import-progress-timeline__title">Still on SalesRidge</span>
                            <span class="import-progress-timeline__desc">
                                Exporting, or SR reports ready but no batch ID in the API yet.
                            </span>
                        </div>
                        <span class="import-progress-timeline__arrow" aria-hidden="true">→</span>
                        <div class="import-progress-timeline__step">
                            <span class="import-progress-timeline__chip import-progress-timeline__chip--ready">
                                Yellow
                            </span>
                            <span class="import-progress-timeline__title">Ready (confirmed)</span>
                            <span class="import-progress-timeline__desc">
                                Batch ID listed as ready_for_export in SalesRidge API — same
                                count as the Enqueue button.
                            </span>
                        </div>
                        <span class="import-progress-timeline__arrow" aria-hidden="true">→</span>
                        <div class="import-progress-timeline__bridge">
                            <span class="import-progress-timeline__bridge-label">Automatic queue or Enqueue</span>
                        </div>
                        <span class="import-progress-timeline__arrow" aria-hidden="true">→</span>
                        <div class="import-progress-timeline__step">
                            <span class="import-progress-timeline__chip import-progress-timeline__chip--queued">
                                Blue
                            </span>
                            <span class="import-progress-timeline__title">Queued</span>
                            <span class="import-progress-timeline__desc">
                                Oliverlist reserved a slot. Still waiting turn — contacts
                                <em>not</em> imported yet.
                            </span>
                        </div>
                        <span class="import-progress-timeline__arrow" aria-hidden="true">→</span>
                        <div class="import-progress-timeline__bridge">
                            <span class="import-progress-timeline__bridge-label">Import runs</span>
                        </div>
                        <span class="import-progress-timeline__arrow" aria-hidden="true">→</span>
                        <div class="import-progress-timeline__step">
                            <span class="import-progress-timeline__chip import-progress-timeline__chip--imported">
                                Green
                            </span>
                            <span class="import-progress-timeline__title">Imported</span>
                            <span class="import-progress-timeline__desc">
                                Contacts are now in Oliverlist for this order.
                            </span>
                        </div>
                    </div>
                </div>
                <LoadingSpinner v-if="loading && tabSelected && !isStandaloneImportTab" :title="loadingTitle" />
                <p
                    v-else-if="!tabSelected"
                    class="text-muted small mb-0 px-2"
                >
                    Select a tab above to load data.
                </p>
                <div
                    v-if="!loading && tabSelected && !isStandaloneImportTab && !activeTableData.length"
                    class="text-muted small mb-0 px-2"
                >
                    {{ emptyMessage }}
                </div>
                <div v-else-if="tabSelected && !isStandaloneImportTab && activeTableData.length" class="row">
                    <div class="col-12">
                        <Table
                            class="mx-2 w-100"
                            :headers="activeTableHeaders"
                            :cols="activeTableCols"
                            :data="activeTableData"
                            :checkbox="false"
                            :notselect="true"
                            :key="tableKey"
                            @openClient="openClient"
                            @openTarget="openTarget"
                            @openTargetSituation="openTargetSituation"
                            @importProgressRowUpdated="onImportProgressRowUpdated"
                            @importPipelineUpdated="onImportPipelineUpdated"
                        />
                        <div
                            v-if="usesImportProgressTableLayout && activePaginatedOrders.length"
                            class="mx-2 mt-2 mb-3 text-center"
                        >
                            <small class="text-muted d-block mb-2">
                                Showing {{ activePaginatedOrders.length }}
                                <span v-if="activePaginatedTotal > 0">
                                    of {{ activePaginatedTotal }}
                                </span>
                                <span
                                    v-if="tabSelected === ACTIVE_IMPORTING_TAB"
                                    class="d-block"
                                    style="font-size: 11px;"
                                >
                                    Incomplete SR import — oldest status date first
                                </span>
                                <span
                                    v-else-if="tabSelected === SEND_EMAIL_PROBLEMS_TAB"
                                    class="d-block"
                                    style="font-size: 11px;"
                                >
                                    Running orders with mailboxes waiting, not synced, or warming up
                                </span>
                            </small>
                            <button
                                v-if="activePaginatedHasMore"
                                type="button"
                                class="btn btn-outline-secondary btn-sm"
                                :disabled="activePaginatedLoadingMore || loading"
                                @click="loadMorePaginatedTab"
                            >
                                <span
                                    v-if="activePaginatedLoadingMore"
                                    class="spinner-border spinner-border-sm mr-1"
                                    role="status"
                                    aria-hidden="true"
                                />
                                Load more
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </main>

        <Modal v-if="isAdmin" name="target-situation-modal" :size="'75'" :scrollable="true">
            <div class="p-4">
                <h5 v-if="situationTarget" class="mb-3">
                    {{ situationTarget.target_name }}
                    <small class="text-muted">#{{ situationTarget.target_id }}</small>
                </h5>
                <TargetCampaignHealthPanel
                    v-if="situationTarget"
                    :key="situationTarget.target_id"
                    :target_id="situationTarget.target_id"
                    :list-tone="situationTarget.health_tone"
                    :list-headline="situationTarget.health_headline"
                    :show-button="false"
                    :auto-load="true"
                    @health-loaded="onSituationHealthLoaded"
                />
            </div>
        </Modal>
    </section>
</template>

<script>
import { mapState, mapActions } from "vuex";
import { targetsService, usersService, clientsService } from "@/_services";
import Table from "@/components/Common/Table.vue";
import Tabs from "@/components/Common/Tabs.vue";
import LoadingSpinner from "@/components/LoadingSpinner.vue";
import FilterSystem from "@/components/FilterSystem.vue";
import Pagination from "@/components/Common/Pagination.vue";
import IndustrySearch from "@/components/AccountCreation/Steps/Campaign/search/IndustrySearch.vue";
import StatsOrder from "@/components/Common/StatsOrder.vue";
import Modal from "@/components/Common/Modal.vue";
import TargetCampaignHealthPanel from "@/components/Targets/TargetCampaignHealthPanel.vue";
import OrphanLinksTab from "@/components/Targets/OrphanLinksTab.vue";
import BookingIntakeTab from "@/components/Targets/BookingIntakeTab.vue";
import DomainsTab from "@/components/Targets/DomainsTab.vue";
import LineChart from "@/components/Chart.vue";

const SITUATION_COL = [
    {
        type: "icon",
        icon_class: "icon-search text-dark",
        value_emit: "target_id",
        type2: "emit",
        emit: "openTargetSituation",
        toneFrom: "health_tone",
        toneTitleFrom: "health_headline",
        toneOverlay: true,
        button_title: "Campaign situation — click for details",
    },
];

const SITUATION_HEADER = {
    label: "",
    icon_class: "icon-search",
    title: "Campaign situation — click for details",
    percentage: "4%",
    class: "overflowhide text-center px-0",
    permission: ["AgencyBilling", "isBookerManager", "isBdr"],
    id: 99,
};

const SR_IMPORT_PROGRESS_COL = [
    {
        type: "salesridgeImportProgress",
        value: "sr_import_progress_pct",
        value2: "sr_import_exported_batches",
        value3: "sr_import_total_batches",
        value4: "sr_import_ready_batches",
        value5: "sr_import_progress_label",
        show_enqueue_button: true,
        enqueue_button_title: "Enqueue ready SalesRidge batches into import.php queue (generatetable)",
        show_launch_button: true,
        launch_button_title: "Launch / relaunch SalesRidge search (searchTarget)",
    },
];

const SR_IMPORT_PROGRESS_HEADER = {
    label: "SalesRidge Import",
    percentage: "16%",
    class: "overflowhide",
    permission: ["AgencyBilling", "isBookerManager", "isBdr"],
    id: 8,
    title: "Green imported · blue queued · yellow ready (API-confirmed) · gray still on SalesRidge",
};

const ACCOUNT_MANAGER_HEADER = {
    label: "Account Manager",
    percentage: "10%",
    class: "overflowhide",
    permission: ["AgencyBilling", "isBookerManager"],
    id: 3,
    title: "Client account manager (oliver_sale)",
};

const ACCOUNT_MANAGER_COL = [
    {
        value: "account_manager_name",
    },
];

const IMPORT_PROGRESS_PAGE_SIZE = 10;
const SEND_EMAIL_PROBLEMS_PAGE_SIZE = 10;

const SALESRIDGE_IMPORT_QUEUE_DAY = 20;
const SALESRIDGE_IMPORT_QUEUE_NIGHT = 40;

const IMPORT_ERRORS_TAB = 1;
const ACTIVE_IMPORTING_TAB = 2;
const SEND_EMAIL_PROBLEMS_TAB = 3;
const ORPHAN_LINKS_TAB = 4;
const BOOKING_INTAKE_TAB = 5;
const DOMAINS_TAB = 6;

export default {
    data() {
        return {
            ORPHAN_LINKS_TAB,
            BOOKING_INTAKE_TAB,
            DOMAINS_TAB,
            loading: false,
            tabSelected: null,
            importTabs: [
                { id: 1, name: "Import errors" },
                { id: 2, name: "Campaign running/preparing waiting SR" },
                { id: 3, name: "Send email problems" },
                { id: 4, name: "Orphan links" },
                { id: 5, name: "Missed meetings" },
                { id: 6, name: "Domains" },
            ],
            fieldsLoading: false,
            orderError: [],
            activeImportingOrders: [],
            activeImportingTotal: 0,
            activeImportingHasMore: false,
            activeImportingNextOffset: 0,
            activeImportingLoadingMore: false,
            sendEmailProblemsOrders: [],
            sendEmailProblemsTotal: 0,
            sendEmailProblemsHasMore: false,
            sendEmailProblemsNextOffset: 0,
            sendEmailProblemsLoadingMore: false,
            importPipeline: null,
            pipelineLoading: false,
            pipelineRefreshing: false,
            situationTarget: null,
            reportSending: false,
            reportMessage: "",
            reportMessageClass: "text-muted",
            tableKey: 100,
            headers: [
                {
                label: "Time",
                class: "overflowhide",
                percentage: "0.1%",
                permission: ["AgencyBilling", "isBookerManager", "isBdr"],
                id: 1,
                },
                {
                    label: this.$t("lang.token.order_id"),
                    percentage: "5%",
                    class: "overflowhide",
                    permission: ["AgencyBilling", "isBookerManager", "isBdr"],
                    id: 1,
                },
                {
                    label: this.$t("lang.token.order_name"),
                    percentage: "0.1%",
                    class: "overflowhide",
                    permission: ["AgencyBilling", "isBookerManager", "isBdr"],
                    id: 1,
                },
                {
                    label: this.$t("lang.token.order_status"),
                    percentage: "0.1%",
                    class: "overflowhide",
                    permission: ["AgencyBilling", "isBookerManager", "isBdr"],
                    id: 2,
                },
                {
                label: this.$t("lang.column.contact_source"),
                percentage: "0.1%",
                class: "overflowhide",
                permission: ["AgencyBilling", "isBookerManager", "isBdr"],
                id: 1,
                },
                {
                    label: "Client",
                    percentage: "18%",
                    class: "overflowhide",
                    permission: ["AgencyBilling", "isBookerManager"],
                    id: 3,
                },

                {
                    label: "total Email sent",
                    percentage: "12.5%",
                    class: "overflowhide",
                    permission: ["AgencyBilling", "isBookerManager", "isBdr"],
                    id: 5,
                },
                {
                    label: "Available Contacts",
                    percentage: "10.5%",
                    class: "overflowhide",
                    permission: ["AgencyBilling", "isBookerManager", "isBdr"],
                    id: 6,
                },
            {
                    label: "utilization Contacts",
                    percentage: "10.5%",
                    class: "overflowhide",
                    permission: ["AgencyBilling", "isBookerManager", "isBdr"],
                    id: 6,
                },
                {
                    label: "SR batches",
                    percentage: "14%",
                    class: "overflowhide",
                    permission: ["AgencyBilling", "isBookerManager", "isBdr"],
                    id: 7,
                    title: "SalesRidge batch import (local DB)",
                },
            ],
            baseCols: [
                [
                    {
                        value: "task_open_hours",
                        type: "time"
                    },
                ],
                [
                    {
                        value: "target_id",
                    },
                ],
                [
                    {
                        value_label: "target_name",
                        value_emit: "target_id",
                        class: "text-primary",
                        type: "emit",
                        emit: "openTarget",
                        badges: [
                            {
                                badge_if: "show_launch_failed_badge",
                                badge_class: "badge badge-danger text-white ml-1",
                                badge_label: "Launch failed",
                                badge_tooltip:
                                    "SalesRidge search failed — excluded from automatic cron. Fix filters and relaunch from the order.",
                            },
                        ],
                    },
                ],

                [
                    {

                        // class: "text-primary",
                        type: "badges",
                        badges: [
                            {
                                badge_if: "target_status_id",
                                badge_value: "1",
                                badge_class: "badge badge-primary text-white",
                                badge_label: this.$t("lang.targets.active"),
                                badge_tooltip: this.$t("lang.orders.status_prepare_tooltip"),
                            },
                            {
                                badge_if: "target_status_id",
                                badge_value: "2",
                                badge_class: "badge badge-danger text-white",
                                badge_label: this.$t("lang.targets.stopped"),
                                badge_tooltip: this.$t("lang.orders.status_inactive_tooltip"),
                            },
                            {
                                badge_if: "target_status_id",
                                badge_value: "3",
                                badge_class: "badge badge-secondary text-white",
                                badge_label: this.$t("lang.targets.pending"),
                                badge_tooltip: this.$t("lang.orders.status_draft_tooltip"),
                            },
                            {
                                badge_if: "target_status_id",
                                badge_value: "4",
                                badge_class: "badge badge-success text-white",
                                badge_label: this.$t("lang.targets.running"),
                                badge_tooltip: this.$t("lang.orders.status_active_tooltip"),
                            },
                            {
                                badge_if: "target_status_id",
                                badge_value: "41",
                                badge_class: "badge badge-danger text-white",
                                badge_label: this.$t("lang.targets.monthlypaused"),
                                badge_tooltip: this.$t("lang.orders.status_active_tooltip"),
                            },
                            {
                                badge_if: "target_status_id",
                                badge_value: "5",
                                badge_class: "badge badge-warning text-white",
                                badge_label: this.$t("lang.targets.archived"),
                                badge_tooltip: this.$t("lang.orders.status_achived_tooltip"),
                            },
                        ],
                    },
                ],
                [
                    {
                        value1: "salesridge",
                        value2: "version",
                        format: 1,
                        type: "twoValueParenthesis"
                    }
                ],
                [
                    {
                        value_label: "client_name",
                        value_emit: "client_id",
                        class: "text-primary",
                        type: "emit",
                        emit: "openClient",
                        badges: [
                            {
                                badge_if: "client_status",
                                badge_value: "2",
                                badge_class: "badge badge-primary text-white",
                                badge_label: this.$t("lang.onBoarding.onboarding"),
                                badge_tooltip: this.$t("lang.label.refused_tooltip"),
                            },

                            {
                                badge_if: "client_status",
                                badge_value: "9",
                                badge_class: "badge badge-warning text-white",
                                badge_label: this.$t("lang.label.validating"),
                                badge_tooltip: this.$t("lang.label.approved_tooltip"),
                            },
                            {
                                badge_if: "client_status",
                                badge_value: "3",
                                badge_class: "badge badge-success text-white",
                                badge_label: this.$t("lang.targets.active"),
                                badge_tooltip: this.$t("lang.label.approved_tooltip"),
                            },
                            {
                                badge_if: "client_status",
                                badge_value: "4",
                                badge_class: "badge badge-success text-white",
                                badge_label: this.$t("lang.label.active"),
                                badge_tooltip: this.$t("lang.label.unverified_tooltip"),
                            },
                            {
                                badge_if: "client_status",
                                badge_value: "5",
                                badge_class: "badge badge-info text-white",
                                badge_label: this.$t("lang.appointments.standby"),
                                badge_tooltip: this.$t("lang.label.unverified_tooltip"),
                            },
                            {
                                badge_if: "client_status",
                                badge_value: "8",
                                badge_class: "badge badge-danger text-white",
                                badge_label: this.$t("lang.label.pendingdeleted"),
                                badge_tooltip: this.$t("lang.label.unverified_tooltip"),
                            },
                            {
                                badge_if: "client_status",
                                badge_value: "7",
                                badge_class: "badge badge-danger text-white",
                                badge_label: this.$t("lang.payment.Recovery"),
                                badge_tooltip: this.$t("lang.label.unverified_tooltip"),
                            },
                            {
                                badge_if: "client_status",
                                badge_value: "6",
                                badge_class: "badge badge-secondary text-white",
                                badge_label: this.$t("lang.label.deleted"),
                                badge_tooltip: this.$t("lang.label.unverified_tooltip"),
                            },
                        ],
                    },
                ],
                [
                    {
                        value: "total_email_sent",
                    },
                ],

                [
                    {
                        value: "available_contacts",
                    },
                ],
                [
                    {
                        value: "id",
                        type: "targetProblem",
                        lazyLoad: true,
                        button_title: "Show contact utilization",
                        small: true,
                    },
                ],
                [
                    {
                        value: "import_batch_detail",
                        value2: "import_batch_summary",
                        type: "importBatchSummary",
                    },
                ],
            ],
        };
    },
    components: {
        Table,
        LoadingSpinner,
        Tabs,
        FilterSystem,
        Pagination,
        StatsOrder,
        IndustrySearch,
        Modal,
        TargetCampaignHealthPanel,
        OrphanLinksTab,
        BookingIntakeTab,
        DomainsTab,
        LineChart,
    },

    computed: {
        ...mapState({
            user: (state) => state.authentication.user,
        }),
        isAdmin() {
            return this.user && parseInt(this.user.role_id, 10) === 1;
        },
        isStandaloneImportTab() {
            return this.tabSelected === ORPHAN_LINKS_TAB
                || this.tabSelected === BOOKING_INTAKE_TAB
                || this.tabSelected === DOMAINS_TAB;
        },
        loadingTitle() {
            if (!this.tabSelected) {
                return "Loading…";
            }
            if (this.tabSelected === ACTIVE_IMPORTING_TAB) {
                return "Loading orders running/preparing waiting SR…";
            }
            if (this.tabSelected === SEND_EMAIL_PROBLEMS_TAB) {
                return "Loading send email problems…";
            }
            if (this.tabSelected === ORPHAN_LINKS_TAB) {
                return "Loading orphan links…";
            }
            if (this.tabSelected === BOOKING_INTAKE_TAB) {
                return "Loading missed meetings…";
            }
            if (this.tabSelected === DOMAINS_TAB) {
                return "Loading domains…";
            }
            return "Loading import errors…";
        },
        emptyMessage() {
            if (!this.tabSelected) {
                return "Select a tab above to load data.";
            }
            if (this.tabSelected === ACTIVE_IMPORTING_TAB) {
                return "No Running or Preparing orders with SalesRidge batches still to import.";
            }
            if (this.tabSelected === SEND_EMAIL_PROBLEMS_TAB) {
                return "No Running orders with mailbox sync or warmup problems blocking sends.";
            }
            return "No orders on Import Error right now.";
        },
        usesImportProgressTableLayout() {
            return this.tabSelected === ACTIVE_IMPORTING_TAB
                || this.tabSelected === SEND_EMAIL_PROBLEMS_TAB;
        },
        activePaginatedOrders() {
            if (this.tabSelected === SEND_EMAIL_PROBLEMS_TAB) {
                return this.sendEmailProblemsOrders;
            }
            if (this.tabSelected === ACTIVE_IMPORTING_TAB) {
                return this.activeImportingOrders;
            }
            return [];
        },
        activePaginatedTotal() {
            if (this.tabSelected === SEND_EMAIL_PROBLEMS_TAB) {
                return this.sendEmailProblemsTotal;
            }
            if (this.tabSelected === ACTIVE_IMPORTING_TAB) {
                return this.activeImportingTotal;
            }
            return 0;
        },
        activePaginatedHasMore() {
            if (this.tabSelected === SEND_EMAIL_PROBLEMS_TAB) {
                return this.sendEmailProblemsHasMore;
            }
            if (this.tabSelected === ACTIVE_IMPORTING_TAB) {
                return this.activeImportingHasMore;
            }
            return false;
        },
        activePaginatedLoadingMore() {
            if (this.tabSelected === SEND_EMAIL_PROBLEMS_TAB) {
                return this.sendEmailProblemsLoadingMore;
            }
            if (this.tabSelected === ACTIVE_IMPORTING_TAB) {
                return this.activeImportingLoadingMore;
            }
            return false;
        },
        activeTableData() {
            if (this.tabSelected === ACTIVE_IMPORTING_TAB) {
                return this.activeImportingOrders;
            }
            if (this.tabSelected === SEND_EMAIL_PROBLEMS_TAB) {
                return this.sendEmailProblemsOrders;
            }
            return this.orderError;
        },
        activeTableHeaders() {
            const headers = [...this.headers];
            if (this.usesImportProgressTableLayout) {
                const srBatchesHeader = headers.pop();
                headers.splice(
                    2,
                    0,
                    SR_IMPORT_PROGRESS_HEADER,
                    ACCOUNT_MANAGER_HEADER,
                    srBatchesHeader
                );
                return headers;
            }
            if (!this.isAdmin) {
                return headers;
            }
            const h = [...headers];
            h.splice(2, 0, SITUATION_HEADER);
            return h;
        },
        activeTableCols() {
            const cols = this.baseCols.map((col) => (Array.isArray(col) ? [...col] : col));
            if (this.usesImportProgressTableLayout) {
                const srBatchesCol = cols.pop();
                cols.splice(2, 0, [{
                    ...SR_IMPORT_PROGRESS_COL[0],
                    show_enqueue_button: this.isAdmin
                        && this.tabSelected === ACTIVE_IMPORTING_TAB,
                    show_launch_button: this.isAdmin
                        && this.tabSelected === ACTIVE_IMPORTING_TAB,
                }], ACCOUNT_MANAGER_COL, srBatchesCol);
                return cols;
            }
            if (!this.isAdmin) {
                return cols;
            }
            const c = cols.map((col) => (Array.isArray(col) ? [...col] : col));
            c.splice(2, 0, SITUATION_COL);
            return c;
        },
        tableHeaders() {
            let h = [...this.headers];
            if (this.isAdmin) {
                h.splice(2, 0, SITUATION_HEADER);
            }
            const clientIdx = h.findIndex((header) => header.label === "Client");
            if (clientIdx >= 0) {
                h.splice(clientIdx + 1, 0, ACCOUNT_MANAGER_HEADER);
            }
            return h;
        },
        tableCols() {
            let c = this.baseCols.map((col) => (Array.isArray(col) ? [...col] : col));
            if (this.isAdmin) {
                c.splice(2, 0, SITUATION_COL);
            }
            const clientIdx = c.findIndex(
                (col) => col[0] && col[0].value_label === "client_name"
            );
            if (clientIdx >= 0) {
                c.splice(clientIdx + 1, 0, ACCOUNT_MANAGER_COL);
            }
            return c;
        },
        dangerOrderCount() {
            return this.orderError.filter((r) => r.health_tone === "danger").length;
        },
        pipelineQueueNightMode() {
            const p = this.importPipeline;
            if (p && typeof p.import_queue_night_mode === "boolean") {
                return p.import_queue_night_mode;
            }
            return this.pipelineQueueMax >= SALESRIDGE_IMPORT_QUEUE_NIGHT;
        },
        pipelineQueueMax() {
            const p = this.importPipeline;
            const fromApi = Number(p && p.import_queue_max);
            if (!Number.isNaN(fromApi) && fromApi > 0) {
                return fromApi;
            }
            const hour = new Date().getHours();
            if (hour >= 22 || hour <= 6) {
                return SALESRIDGE_IMPORT_QUEUE_NIGHT;
            }
            return SALESRIDGE_IMPORT_QUEUE_DAY;
        },
        pipelineImportLine() {
            const p = this.importPipeline;
            if (!p) {
                return "";
            }
            const workerMax = this.pipelineQueueMax;
            const modeLabel = this.pipelineQueueNightMode ? "night" : "day";
            const parts = [
                "import.php — " + workerMax + " workers (" + modeLabel + "), one batch per slot",
            ];
            parts.push((p.imported_last_1h || 0) + " batches imported last hour");
            parts.push((p.imported_last_24h || 0) + " imported last 24h");
            if (p.last_import_ago) {
                parts.push("last import " + p.last_import_ago);
            }
            return parts.join(" · ");
        },
        pipelineReadyForExportLine() {
            const p = this.importPipeline;
            if (!p || p.ready_for_export_total == null) {
                return "";
            }
            const total = Number(p.ready_for_export_total);
            if (Number.isNaN(total)) {
                return "";
            }
            return total.toLocaleString() + " batches ready for export";
        },
        pipelineQueueSlotsLabel() {
            const p = this.importPipeline;
            if (!p) {
                return "";
            }
            const used = Number(p.pending_batches) || 0;
            const max = this.pipelineQueueMax;
            const free = Number(p.import_queue_free);
            const freeSlots = free >= 0 ? free : Math.max(0, max - used);
            const orderCount = Number(p.orders_in_queue) || 0;
            let label = used + "/" + max + " slots occupied";
            if (freeSlots > 0) {
                label += " · " + freeSlots + " free";
            } else if (used >= max) {
                label += " · queue full";
            }
            if (orderCount > 0) {
                label += " · " + orderCount + " order(s) waiting";
            }
            return label;
        },
        pipelineQueueSlots() {
            const p = this.importPipeline;
            const max = this.pipelineQueueMax;
            const workers = (p && p.workers) || [];
            const byPosition = {};
            workers.forEach((w) => {
                const pos = Number(w.position);
                if (!Number.isNaN(pos)) {
                    byPosition[pos] = w;
                }
            });
            const slots = [];
            for (let position = 0; position < max; position += 1) {
                const worker = byPosition[position];
                const occupied = !!worker;
                let title = "Slot " + position + " — empty";
                if (occupied) {
                    const orderId = worker.salesridge_id
                        ? String(worker.salesridge_id).split("-")[0]
                        : "?";
                    title = "Slot " + position + " — order #" + orderId
                        + ", batch " + (worker.batch_id || "?");
                    if (worker.queued_at) {
                        title += ", queued " + worker.queued_at;
                    }
                }
                slots.push({ position, occupied, title, nightSlot: position >= SALESRIDGE_IMPORT_QUEUE_DAY });
            }
            return slots;
        },
        pipelineThroughputLine() {
            const p = this.importPipeline;
            if (!p) {
                return "";
            }
            const imported1h = Number(p.imported_last_1h) || 0;
            const perMin1h = Number(p.batches_per_minute_1h);
            const perMin = !Number.isNaN(perMin1h) && perMin1h > 0
                ? perMin1h
                : (imported1h > 0 ? Math.round((imported1h / 60) * 100) / 100 : 0);
            const parts = [];
            if (imported1h > 0) {
                parts.push(
                    "~" + perMin + " batches/min avg (last hour — each finished import counts as 1 batch)"
                );
            } else {
                parts.push("0 batches imported in the last hour");
            }
            const perMin24h = Number(p.batches_per_minute_24h);
            if (!Number.isNaN(perMin24h) && perMin24h > 0 && Number(p.imported_last_24h) > 0) {
                parts.push("~" + perMin24h + "/min over 24h");
            }
            return parts.join(" · ");
        },
        pipelineEnqueueLine() {
            const p = this.importPipeline;
            if (!p) {
                return "";
            }
            const parts = [
                "generatetable.php — enqueues SR batches into the queue above",
            ];
            const starving = p.starving_for_enqueue || 0;
            const tabTotal = Number(p.import_in_progress_tab_sql) || 0;
            const incompleteLocal = Number(p.import_incomplete_local_sql) || 0;
            if (starving > 0) {
                parts.push(
                    starving + " order(s) with 0 local batches (starving — priority for enqueue)"
                );
            } else if (tabTotal > 0) {
                parts.push(
                    tabTotal + " on waiting SR tab"
                    + (incompleteLocal > 0
                        ? " · " + incompleteLocal + " with queue or no local batch yet"
                        : "")
                    + " — many wait on SalesRidge export or ready_for_export, not starving"
                );
            } else {
                parts.push("no import-in-progress candidates in SQL");
            }
            if (p.generatetable_cron) {
                parts.push("cron: " + p.generatetable_cron);
            }
            return parts.join(" · ");
        },
        pipelineSummaryText() {
            const p = this.importPipeline;
            if (!p) {
                return "";
            }
            return this.pipelineImportLine + " | " + this.pipelineEnqueueLine;
        },
        pipelineHourChartKey() {
            const buckets = this.importPipeline
                && this.importPipeline.imported_last_hour_buckets
                && this.importPipeline.imported_last_hour_buckets.buckets;
            if (!buckets || !buckets.length) {
                return "pipeline-hour-empty";
            }
            return buckets.map((b) => b.count).join(",");
        },
        pipelineHourChartData() {
            const bucketPayload = this.importPipeline
                && this.importPipeline.imported_last_hour_buckets;
            const buckets = bucketPayload && bucketPayload.buckets;
            if (!buckets || !buckets.length) {
                return null;
            }
            return {
                labels: buckets.map((b) => b.label),
                datasets: [
                    {
                        type: "bar",
                        label: "Batches imported",
                        backgroundColor: "RGBA(54, 162, 235, 0.55)",
                        borderColor: "RGBA(54, 162, 235, 1)",
                        borderWidth: 1,
                        data: buckets.map((b) => Number(b.count) || 0),
                    },
                ],
            };
        },
        pipelineHourChartOptions() {
            const bucketPayload = this.importPipeline
                && this.importPipeline.imported_last_hour_buckets;
            const buckets = (bucketPayload && bucketPayload.buckets) || [];
            const maxCount = buckets.reduce(
                (max, bucket) => Math.max(max, Number(bucket.count) || 0),
                0
            );
            return {
                responsive: true,
                maintainAspectRatio: false,
                legend: { display: false },
                scales: {
                    xAxes: [
                        {
                            ticks: {
                                fontSize: 9,
                                maxRotation: 0,
                                autoSkip: false,
                            },
                            gridLines: { display: false },
                        },
                    ],
                    yAxes: [
                        {
                            ticks: {
                                beginAtZero: true,
                                precision: 0,
                                fontSize: 9,
                                suggestedMax: Math.max(5, Math.ceil(maxCount * 1.15)),
                            },
                        },
                    ],
                },
                tooltips: {
                    callbacks: {
                        title(tooltipItems) {
                            const idx = tooltipItems[0] && tooltipItems[0].index;
                            const bucket = buckets[idx];
                            if (!bucket) {
                                return "";
                            }
                            if (bucket.minutes_ago_end === 0) {
                                return "Last 5 minutes";
                            }
                            return bucket.minutes_ago_start + "–" + bucket.minutes_ago_end + " min ago";
                        },
                        label(tooltipItem) {
                            const count = Number(tooltipItem.yLabel) || 0;
                            const batchLabel = count === 1 ? "batch" : "batches";
                            return count + " " + batchLabel;
                        },
                    },
                },
            };
        },
        pipelineActiveText() {
            if (!this.importPipeline || !this.importPipeline.is_importing_now) {
                return "";
            }

            const workers = this.importPipeline.workers || [];
            if (!workers.length) {
                return "Workers are processing — counts update when each batch finishes.";
            }

            const byOrder = {};
            workers.forEach((w) => {
                const raw = w.salesridge_id ? String(w.salesridge_id) : "";
                const orderId = raw.indexOf("-") >= 0 ? raw.split("-")[0] : raw || "?";
                byOrder[orderId] = (byOrder[orderId] || 0) + 1;
            });

            const entries = Object.keys(byOrder).map((id) => ({
                id,
                count: byOrder[id],
            }));

            const orderList = entries
                .map((order) => {
                    const batchLabel = order.count === 1 ? "batch" : "batches";
                    return "order #" + order.id + " (" + order.count + " " + batchLabel + ")";
                })
                .join(", ");

            return "Right now: " + orderList + " — hourly totals rise as each batch finishes.";
        },
    },

    mounted() {
        this.setHeader();
        this.loadImportPipeline();
    },
    methods: {
        ...mapActions("headertopbar", {
            setCurrentRoute:"setRoute",
            setCurrentRouteChild:"setRouteChild"
        }),
        setHeader(){
            this.setCurrentRoute({ page: "import-error", label: "Import Error" });
            this.setCurrentRouteChild({ child: "" });
        },
        changeTab(tabId) {
            if (this.tabSelected === tabId) {
                return;
            }
            this.tabSelected = tabId;
            this.tableKey += 1;
            this.loadActiveTabData();
        },
        async loadActiveTabData() {
            if (!this.tabSelected) {
                return;
            }
            if (this.tabSelected === ACTIVE_IMPORTING_TAB) {
                await this.getActiveImporting();
            } else if (this.tabSelected === SEND_EMAIL_PROBLEMS_TAB) {
                await this.getTargetSendEmailProblems();
            } else if (this.tabSelected === IMPORT_ERRORS_TAB) {
                await this.getTargetImportError();
            } else if (this.tabSelected === ORPHAN_LINKS_TAB) {
                return;
            } else if (this.tabSelected === BOOKING_INTAKE_TAB) {
                return;
            } else if (this.tabSelected === DOMAINS_TAB) {
                return;
            }
        },
        mapImportRows(rows, withHealthTone = true) {
            return rows.map((row) => {
                const tone = row.import_progress_row_tone || "";
                let rowHighlightClass = "";
                if (tone === "warmup") {
                    rowHighlightClass = "import-progress-row--warmup";
                } else if (tone === "no_salesridge") {
                    rowHighlightClass = "import-progress-row--no-sr";
                } else if (tone === "no_salesridge_blocked") {
                    rowHighlightClass = "import-progress-row--no-sr-blocked";
                } else if (tone === "no_salesridge_running") {
                    rowHighlightClass = "import-progress-row--no-sr-running";
                }
                return {
                    ...row,
                    row_highlight_class: rowHighlightClass,
                    show_launch_failed_badge:
                        Number(row.has_salesridge_launch_error) === 1,
                    health_tone: withHealthTone ? (row.health_tone || "loading") : "",
                    health_headline: withHealthTone ? (row.health_headline || "") : "",
                };
            });
        },
        onImportProgressRowUpdated({ target_id, row }) {
            this.applyImportProgressRow(target_id, row);
        },
        loadImportProgressBreakdowns() {
            const rows = this.activePaginatedOrders.slice();
            let index = 0;

            const loadNext = () => {
                if (
                    index >= rows.length
                    || !this.usesImportProgressTableLayout
                ) {
                    return;
                }

                const row = rows[index];
                index += 1;
                const targetId = row && row.target_id;
                if (!targetId || Number(row.sr_import_breakdown_loading) !== 1) {
                    loadNext();
                    return;
                }

                targetsService.getTargetSalesridgeImportProgressRow(targetId)
                    .then((refreshed) => {
                        if (refreshed) {
                            this.applyImportProgressRow(targetId, refreshed);
                        }
                    })
                    .catch((err) => {
                        const list = this.activePaginatedOrders;
                        const idx = list.findIndex(
                            (r) => String(r.target_id) === String(targetId)
                        );
                        if (idx >= 0) {
                            const reason = err && typeof err === "object" && err.message
                                ? String(err.message)
                                : "";
                            const label = reason === "not_eligible" || reason === "target_not_found"
                                ? "SalesRidge breakdown unavailable for this row."
                                : "Could not load SalesRidge breakdown.";
                            this.$set(list[idx], "sr_import_progress_label", label);
                            this.$set(list[idx], "sr_import_breakdown_loading", 0);
                        }
                    })
                    .finally(() => {
                        loadNext();
                    });
            };

            loadNext();
        },
        applyImportProgressRow(targetId, row) {
            const list = this.activePaginatedOrders;
            const idx = list.findIndex(
                (r) => String(r.target_id) === String(targetId)
            );
            if (idx < 0 || !row) {
                return;
            }

            const mapped = this.mapImportRows([row], false)[0];

            if (this.tabSelected === ACTIVE_IMPORTING_TAB) {
                this.$set(this.activeImportingOrders, idx, mapped);
            } else {
                this.$set(this.sendEmailProblemsOrders, idx, mapped);
            }
        },
        onImportPipelineUpdated(pipeline) {
            if (!this.importPipeline || !pipeline) {
                return;
            }
            this.importPipeline = {
                ...this.importPipeline,
                pending_batches: pipeline.pending_total,
                is_importing_now: pipeline.is_importing_now,
            };
        },
        getActiveImporting(append) {
            const isAppend = append === true;
            if (isAppend) {
                this.activeImportingLoadingMore = true;
            } else {
                this.loading = true;
                this.activeImportingOrders = [];
                this.activeImportingTotal = 0;
                this.activeImportingHasMore = false;
                this.activeImportingNextOffset = 0;
            }

            const offset = isAppend ? this.activeImportingNextOffset : 0;

            return targetsService
                .getTargetImportProgress({
                    limit: IMPORT_PROGRESS_PAGE_SIZE,
                    offset: offset,
                    importStillPending: true,
                })
                .then((res) => {
                    const payload = res && res.orders ? res : { orders: res, pipeline: null };
                    const rows = Array.isArray(payload.orders) ? payload.orders : [];
                    if (payload.pagination) {
                        this.activeImportingTotal = Number(payload.pagination.total) || 0;
                        this.activeImportingHasMore = !!payload.pagination.has_more;
                        this.activeImportingNextOffset =
                            Number(payload.pagination.next_offset) || 0;
                    } else {
                        this.activeImportingTotal = rows.length;
                        this.activeImportingHasMore = false;
                        this.activeImportingNextOffset = rows.length;
                    }
                    const mapped = this.mapImportRows(rows, false);
                    if (isAppend) {
                        this.activeImportingOrders = this.activeImportingOrders.concat(mapped);
                    } else {
                        this.activeImportingOrders = mapped;
                    }
                    this.loadImportProgressBreakdowns();
                })
                .catch(() => {
                    if (!isAppend) {
                        this.activeImportingOrders = [];
                        this.activeImportingTotal = 0;
                        this.activeImportingHasMore = false;
                        this.activeImportingNextOffset = 0;
                    }
                })
                .finally(() => {
                    if (isAppend) {
                        this.activeImportingLoadingMore = false;
                    } else {
                        this.loading = false;
                    }
                });
        },
        loadMorePaginatedTab() {
            if (
                this.activePaginatedLoadingMore
                || this.loading
                || !this.activePaginatedHasMore
            ) {
                return;
            }
            if (this.tabSelected === SEND_EMAIL_PROBLEMS_TAB) {
                return this.getTargetSendEmailProblems(true);
            }
            if (this.tabSelected === ACTIVE_IMPORTING_TAB) {
                return this.getActiveImporting(true);
            }
        },
        getTargetSendEmailProblems(append) {
            const isAppend = append === true;
            if (isAppend) {
                this.sendEmailProblemsLoadingMore = true;
            } else {
                this.loading = true;
                this.sendEmailProblemsOrders = [];
                this.sendEmailProblemsTotal = 0;
                this.sendEmailProblemsHasMore = false;
                this.sendEmailProblemsNextOffset = 0;
            }

            const offset = isAppend ? this.sendEmailProblemsNextOffset : 0;

            return targetsService
                .getTargetSendEmailProblems({
                    limit: SEND_EMAIL_PROBLEMS_PAGE_SIZE,
                    offset: offset,
                })
                .then((res) => {
                    const payload = res && res.orders ? res : { orders: res, pipeline: null };
                    const rows = Array.isArray(payload.orders) ? payload.orders : [];
                    if (payload.pagination) {
                        this.sendEmailProblemsTotal = Number(payload.pagination.total) || 0;
                        this.sendEmailProblemsHasMore = !!payload.pagination.has_more;
                        this.sendEmailProblemsNextOffset =
                            Number(payload.pagination.next_offset) || 0;
                    } else {
                        this.sendEmailProblemsTotal = rows.length;
                        this.sendEmailProblemsHasMore = false;
                        this.sendEmailProblemsNextOffset = rows.length;
                    }
                    const mapped = this.mapImportRows(rows, false);
                    if (isAppend) {
                        this.sendEmailProblemsOrders = this.sendEmailProblemsOrders.concat(mapped);
                    } else {
                        this.sendEmailProblemsOrders = mapped;
                    }
                    this.loadImportProgressBreakdowns();
                })
                .catch(() => {
                    if (!isAppend) {
                        this.sendEmailProblemsOrders = [];
                        this.sendEmailProblemsTotal = 0;
                        this.sendEmailProblemsHasMore = false;
                        this.sendEmailProblemsNextOffset = 0;
                    }
                })
                .finally(() => {
                    if (isAppend) {
                        this.sendEmailProblemsLoadingMore = false;
                    } else {
                        this.loading = false;
                    }
                });
        },
        getTargetImportError() {
            this.loading = true;
            return targetsService
                .getTargetImportError()
                .then((res) => {
                    const payload = res && res.orders ? res : { orders: res, pipeline: null };
                    const rows = Array.isArray(payload.orders) ? payload.orders : [];
                    this.orderError = this.mapImportRows(rows);
                    if (this.isAdmin) {
                        this.loadImportErrorHealthTones();
                    }
                })
                .catch(() => {
                    this.orderError = [];
                })
                .finally(() => {
                    this.loading = false;
                });
        },

        loadImportPipeline() {
            if (this.pipelineLoading || this.pipelineRefreshing) {
                return Promise.resolve();
            }
            this.pipelineLoading = true;
            return targetsService
                .getTargetImportError({ pipelineOnly: true })
                .then((res) => {
                    if (res && res.pipeline) {
                        this.importPipeline = res.pipeline;
                    }
                })
                .finally(() => {
                    this.pipelineLoading = false;
                });
        },

        refreshImportPipeline() {
            if (this.pipelineRefreshing) {
                return;
            }
            this.pipelineRefreshing = true;
            return targetsService
                .getTargetImportError({ pipelineOnly: true })
                .then((res) => {
                    if (res && res.pipeline) {
                        this.importPipeline = res.pipeline;
                    }
                })
                .finally(() => {
                    this.pipelineRefreshing = false;
                });
        },

        loadImportErrorHealthTones() {
            const rows = this.orderError.slice();
            let index = 0;

            const loadNext = () => {
                if (index >= rows.length) {
                    return;
                }

                const row = rows[index];
                index += 1;
                const targetId = row.target_id;
                if (!targetId) {
                    loadNext();
                    return;
                }
                if (row.show_launch_failed_badge) {
                    loadNext();
                    return;
                }

                targetsService
                    .getCampaignHealthTones([targetId], { import_error_list: true })
                    .then((res) => {
                        const entry =
                            res && res[String(targetId)] ? res[String(targetId)] : null;
                        if (entry) {
                            this.applyImportErrorHealthTone(targetId, entry);
                        } else {
                            this.applyImportErrorHealthTone(targetId, {
                                tone: "warning",
                                headline: "Could not load order situation.",
                            });
                        }
                    })
                    .catch(() => {
                        this.applyImportErrorHealthTone(targetId, {
                            tone: "warning",
                            headline: "Could not load order situation.",
                        });
                    })
                    .finally(() => {
                        loadNext();
                    });
            };

            loadNext();
        },

        applyImportErrorHealthTone(targetId, entry) {
            const idx = this.orderError.findIndex(
                (r) => String(r.target_id) === String(targetId)
            );
            if (idx < 0) {
                return;
            }
            this.$set(this.orderError[idx], "health_tone", entry.tone || "warning");
            this.$set(
                this.orderError[idx],
                "health_headline",
                entry.headline || ""
            );
            this.tableKey += 1;
        },

        reportProblems() {
            if (!this.dangerOrderCount || this.reportSending) {
                return;
            }
            this.reportSending = true;
            this.reportMessage = "";
            targetsService
                .reportImportErrorProblems()
                .then((res) => {
                    const count = res && res.targets != null ? res.targets : this.orderError.length;
                    const sent = res && res.sent != null ? res.sent : 0;
                    this.reportMessageClass = "text-success";
                    this.reportMessage =
                        "Report sent for " + count + " order(s) to " + sent + " recipient(s).";
                })
                .catch((err) => {
                    this.reportMessageClass = "text-danger";
                    this.reportMessage =
                        (err && err.message) || "Could not send the report.";
                })
                .finally(() => {
                    this.reportSending = false;
                });
        },

        openClient(clientId) {
            let url = '/clients/' + clientId;
            window.open(url, '_blank');
        },
        openTarget(targetId) {
            let url = '/orders/' + targetId;
            window.open(url, '_blank');
        },
        openTargetSituation(targetId) {
            const row = this.activeTableData.find(
                (r) => String(r.target_id) === String(targetId)
            );
            if (!row) {
                return;
            }
            this.situationTarget = row;
            this.$modal.show("target-situation-modal");
        },

        onSituationHealthLoaded(payload) {
            if (!payload || payload.target_id == null) {
                return;
            }
            const targetId = String(payload.target_id);
            if (
                !this.situationTarget ||
                String(this.situationTarget.target_id) !== targetId
            ) {
                return;
            }
            // Keep the list magnifier headline — full diagnosis can still say "assign pending".
            if (
                payload.headline &&
                this.situationTarget.health_tone !== "ok" &&
                !this.situationTarget.show_launch_failed_badge
            ) {
                this.$set(this.situationTarget, "health_headline", payload.headline);
            }
        },
    },

};
</script>

<style scoped>
.banner {
    border-radius: 25em !important;
    color: #fff;
    font-size: 0.75em;
    text-align: left;
    margin-left: 1em;
    margin-right: 1em;
    margin-bottom: 0.2em;
    padding-top: 0.5em;
    padding-bottom: 0.5em;
    background-color: #4d4deb;
    width: 98%;
}

.magnifier-legend .situation-tone-legend {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 2rem;
    height: 2rem;
    border-radius: 0.25rem;
    position: relative;
}

.magnifier-legend .situation-tone-badge {
    position: absolute;
    right: -0.15rem;
    bottom: -0.1rem;
    font-size: 0.65rem;
    line-height: 1;
    background: #fff;
    border-radius: 50%;
    padding: 0.05rem;
}

.magnifier-legend .situation-tone-badge--warning {
    color: #f9a825;
}

.magnifier-legend .situation-tone-badge--danger {
    color: #c62828;
}

.magnifier-legend .situation-tone--ok {
    background: #e8f5e9;
    border: 1px solid #a5d6a7;
    color: #2e7d32;
}

.magnifier-legend .situation-tone--warning {
    background: #fff8e1;
    border: 1px solid #ffe082;
    color: #f9a825;
}

.magnifier-legend .situation-tone--danger {
    background: #ffebee;
    border: 1px solid #ef9a9a;
    color: #c62828;
}

.import-progress-legend .import-progress-timeline {
    display: flex;
    flex-wrap: wrap;
    align-items: flex-start;
    gap: 0.35rem 0.25rem;
}

.import-progress-legend .import-progress-timeline__step {
    flex: 1 1 7.5rem;
    min-width: 7rem;
    max-width: 11rem;
    text-align: center;
}

.import-progress-legend .import-progress-timeline__chip {
    display: inline-block;
    padding: 0.2rem 0.55rem;
    border-radius: 0.25rem;
    font-weight: 600;
    font-size: 0.8rem;
    color: #fff;
    margin-bottom: 0.25rem;
}

.import-progress-legend .import-progress-timeline__chip--pending {
    background: #6c757d;
}

.import-progress-legend .import-progress-timeline__chip--ready {
    background: #ffc107;
    color: #212529;
}

.import-progress-legend .import-progress-timeline__chip--queued {
    background: #17a2b8;
}

.import-progress-legend .import-progress-timeline__chip--imported {
    background: #28a745;
}

.import-progress-legend .import-progress-timeline__title {
    display: block;
    font-weight: 600;
    line-height: 1.25;
}

.import-progress-legend .import-progress-timeline__desc {
    display: block;
    color: #6c757d;
    font-size: 0.8rem;
    line-height: 1.35;
    margin-top: 0.15rem;
}

.import-progress-legend .import-progress-timeline__arrow {
    flex: 0 0 auto;
    align-self: center;
    color: #adb5bd;
    font-size: 1.25rem;
    font-weight: 300;
    line-height: 1;
    padding: 0 0.1rem;
}

.import-progress-legend .import-progress-timeline__bridge {
    flex: 0 1 5.5rem;
    min-width: 4.5rem;
    align-self: center;
    text-align: center;
}

.import-progress-legend .import-progress-timeline__bridge-label {
    display: block;
    font-size: 0.7rem;
    line-height: 1.25;
    color: #868e96;
    font-style: italic;
    border-top: 1px dashed #ced4da;
    border-bottom: 1px dashed #ced4da;
    padding: 0.2rem 0.15rem;
}

.import-tone-swatch {
    display: inline-block;
    width: 0.75rem;
    height: 0.75rem;
    border-radius: 0.15rem;
    margin-right: 0.35rem;
    vertical-align: -0.1rem;
}

.import-tone-swatch--launch {
    background-color: #ffebee;
    border: 1px solid #ef9a9a;
}

.import-tone-swatch--blocked {
    background-color: #fff8e1;
    border: 1px solid #ffe082;
}

.import-tone-swatch--running {
    background-color: #e8f5e9;
    border: 1px solid #a5d6a7;
}

.pipeline-hour-chart-wrap {
    max-width: 100%;
    height: 6.5rem;
}

.pipeline-slot-row {
    gap: 0.25rem;
}

.pipeline-slot-row--night .pipeline-slot {
    width: 1.45rem;
    height: 1.2rem;
    font-size: 0.6rem;
}

.pipeline-slot {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 1.65rem;
    height: 1.35rem;
    border-radius: 0.2rem;
    font-size: 0.65rem;
    font-weight: 600;
    line-height: 1;
    border: 1px solid transparent;
}

.pipeline-slot--free {
    background: #e9ecef;
    border-color: #ced4da;
    color: #868e96;
}

.pipeline-slot--occupied {
    background: #17a2b8;
    border-color: #138496;
    color: #fff;
}

.pipeline-slot--night-extra {
    box-shadow: -2px 0 0 0 #adb5bd;
}

.banner-message {
    margin-left: 2em;
    margin-right: 2em;
}

.column-sales {
    width: 1200px !important;
    white-space: nowrap;
}
</style>
